import { c as _c } from "react/compiler-runtime";
import { useTranslationContext } from 'platform/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isNilOrEmpty } from 'ramda-adjunct';
import { isNotNilOrEmpty, useBoolean } from 'shared/web';
import { useHttpCalls } from '../api/useHttpCalls';
import { selectChoiceFilterOptions, selectGridCode } from '../store/createEagGridStore';
import { useEagGridSelector } from '../store/useEagGridSelector';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ChoiceFilterOptions } from '../types/Api';
const EMPTY_OPTIONS: ChoiceFilterOptions = [];
type UseLazyChoiceFilterOptionsProps = {
  columnKey: string;
};

/**
 * Lazily loads choice filter options and caches them in EagGrid store state.
 * Cached options survive panel close/reopen — no redundant network requests.
 *
 * `loadOptions` has a **stable identity** (never changes between renders) because
 * its only dependency is `store` (ref-stable). All volatile values (gridCode,
 * columnKey, http, cachedOptions) are read at call time via `store.getState()`
 * and `httpRef.current` — not captured in the closure.
 *
 * @see Vercel advanced-event-handler-refs pattern
 */
export function useLazyChoiceFilterOptions(props) {
  const $ = _c(15);
  const store = useEagGridStore();
  let t0;
  if ($[0] !== props.columnKey) {
    t0 = selectChoiceFilterOptions(props.columnKey);
    $[0] = props.columnKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const cachedOptions = useEagGridSelector(t0);
  const http = useHttpCalls();
  const t = useTranslationContext();
  const [isLoading, startLoading, stopLoading] = useBoolean();
  const [errorMessage, setErrorMessage] = useState(null);
  const httpRef = useRef(http);
  const columnKeyRef = useRef(props.columnKey);
  const tRef = useRef(t);
  let t1;
  if ($[2] !== http || $[3] !== props.columnKey || $[4] !== t) {
    t1 = () => {
      httpRef.current = http;
      columnKeyRef.current = props.columnKey;
      tRef.current = t;
    };
    $[2] = http;
    $[3] = props.columnKey;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEffect(t1);
  let t2;
  if ($[6] !== startLoading || $[7] !== stopLoading || $[8] !== store) {
    t2 = () => {
      const gridCode = selectGridCode(store.getState());
      const columnKey = columnKeyRef.current;
      const cached = selectChoiceFilterOptions(columnKey)(store.getState());
      if (isNilOrEmpty(gridCode) || isNilOrEmpty(columnKey)) {
        return Promise.resolve(EMPTY_OPTIONS);
      }
      if (isNotNilOrEmpty(cached)) {
        setErrorMessage(null);
        stopLoading();
        return Promise.resolve(cached as ChoiceFilterOptions);
      }
      startLoading();
      setErrorMessage(null);
      return httpRef.current.getColumnFilterOptions(columnKey).then(response => {
        const options = response ?? EMPTY_OPTIONS;
        setErrorMessage(null);
        store.setChoiceFilterOptions(columnKey, options);
      }).catch(() => {
        setErrorMessage(tRef.current("general.errors.cannotLoadOptions"));
      }).finally(() => {
        stopLoading();
      });
    };
    $[6] = startLoading;
    $[7] = stopLoading;
    $[8] = store;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const loadOptions = t2;
  const t3 = cachedOptions ?? EMPTY_OPTIONS;
  let t4;
  if ($[10] !== errorMessage || $[11] !== isLoading || $[12] !== loadOptions || $[13] !== t3) {
    t4 = {
      options: t3,
      isLoading,
      errorMessage,
      loadOptions
    };
    $[10] = errorMessage;
    $[11] = isLoading;
    $[12] = loadOptions;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}